/**
 * Reusable on-chain verifiers.
 *
 * A verifier answers one question: "has this step's effect already happened on
 * chain?" One `eth_call`, no transaction hash required — which makes it the
 * only workable recovery path when the tab died before the hash came back, and
 * the only one at all for ERC-4337 smart accounts whose EVM nonce never moves.
 */

const ERC20_ALLOWANCE_ABI = [
  {
    constant: true,
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" },
    ],
    name: "allowance",
    outputs: [{ name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function",
  },
];

/**
 * Generic ERC-20 approve verifier, driven entirely by what we journaled:
 * `request.to` is the token, `request.args` is `[spender, amount]`.
 *
 * Nine of the eighteen signing surfaces start with an `approve`, so this one
 * function covers all of them without any per-screen wiring.
 *
 * Note it answers "is the allowance sufficient", not "did MY transaction land".
 * That is the semantically correct question for a recovery decision: if the
 * allowance is already there, the approve step is satisfied and the flow should
 * move on, regardless of which transaction provided it.
 */
export async function approveSatisfied({ client, entry }) {
  const request = entry && entry.request;
  if (!request || !request.to || !Array.isArray(request.args) || request.args.length < 2) {
    return false;
  }
  const [spender, amount] = request.args;
  if (!spender || amount == null) return false;

  const allowance = await client.readContract({
    address: request.to,
    abi: ERC20_ALLOWANCE_ABI,
    functionName: "allowance",
    args: [entry.address, spender],
  });

  try {
    // eslint-disable-next-line no-undef
    return BigInt(allowance) >= BigInt(amount);
  } catch (err) {
    return false;
  }
}

const GET_REQUEST_ABI = [
  {
    inputs: [{ name: "_requestId", type: "uint256" }],
    name: "getRequest",
    outputs: [
      {
        components: [
          { name: "id", type: "uint256" },
          { name: "user", type: "address" },
          { name: "custodian", type: "address" },
          { name: "userAmountDAI", type: "uint256" },
          { name: "custodianLockedAmount", type: "uint256" },
          { name: "phase", type: "uint256" },
          { name: "oraclePrice", type: "uint256" },
          { name: "userAmountToken", type: "uint256" },
          { name: "requestedToken", type: "address" },
          { name: "finalized", type: "bool" },
          { name: "isDeposit", type: "bool" },
          { name: "createdAt", type: "uint256" },
        ],
        name: "",
        type: "tuple",
      },
    ],
    stateMutability: "view",
    type: "function",
  },
];

/**
 * "Has this request already reached the stage this step was going to open?"
 *
 * `Request.phase` — 0 regular, 1 dispute, 2 appeal, 3 resolved, 4 completed,
 * 5 cancelled (`FrdmStructs.sol:57`). One `eth_call`, no transaction hash
 * needed, which makes it the only recovery path that works when the tab died
 * before the hash came back, and the only one at all for ERC-4337 smart
 * accounts whose EVM nonce never moves.
 *
 * This matters most on the APPEAL step. `openDispute` reverts
 * `RequestHasDispute()` on a second call, so the chain refuses the duplicate
 * payment by itself. `openAppeal` has no such guard and will take another
 * 10 DAI, so this verifier — together with the server gate and the pre-send
 * re-read in `useStageFlow` — is what stands in for one.
 *
 * `entry.request.args[0]` is the requestId for both `openDispute(requestId)`
 * and `openAppeal(requestId, disputeId)`.
 */
export function stageAlreadyOpen(expectedPhase) {
  return async function verify({ client, entry }) {
    const request = entry && entry.request;
    if (!request || !request.to || !Array.isArray(request.args) || !request.args.length) {
      return false;
    }
    const requestId = request.args[0];
    if (requestId == null) return false;

    const result = await client.readContract({
      address: request.to,
      abi: GET_REQUEST_ABI,
      functionName: "getRequest",
      args: [requestId],
    });

    const phase = tupleField(result, "phase", 5);
    if (phase == null) return false;
    try {
      // eslint-disable-next-line no-undef
      return BigInt(phase) === BigInt(expectedPhase);
    } catch (err) {
      return false;
    }
  };
}

/**
 * Read a tuple return that viem may hand back either as a named object or as a
 * positional array, depending on ABI metadata.
 */
export function tupleField(result, name, index) {
  if (result == null) return undefined;
  if (Array.isArray(result)) return result[index];
  if (typeof result === "object" && name in result) return result[name];
  return undefined;
}
